import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import useCreateChellangeStudySession from "../queries/use-create-challenge-study-session"
import useUpdateChellangeStudySession from "../queries/use-update-challenge-study-session"
import type { ChallengeActivity } from "@/services/api/odin/activities/challenge/study-session/update"
import { usePreventNavigation } from "@/modules/activities/hooks/use-prevent-navigation"

import Header from "./header"
import Content from "./content"
import Footer from "./footer"

import ChallengeFeedbackHeader from "./header/challenge-feedback-header"
import ChallengeFeedbackContent from "./content/challenge-feedback"
import ChallengeFeedbackFooter from "./footer/challenge-feedback-footer"

import ChallengeQuestions from "./content/challenge-questions"
import ChallengeQuestionsHeader from "./header/challenge-questions-header"
import { useLeftActivityAlertStore } from "../../store/use-left-activity-alert"
import useInvalidateStudyPlannerQueries from "../../hooks/use-invalidate-queries"
import { PRODUCT_TOUR_V2_STEPS } from "@/modules/product-tour-v2/types/steps"
import { useProductTourV2 } from "@/modules/product-tour-v2/stores/use-product-tour"
import { useAuth } from "@/modules/auth/store"

const activitiyInitial: ChallengeActivity = {
  id: "",
  feedback: {
    approvalScore: 0,
    scorePercentage: 0,
    bullets: [],
  },
  review: {
    correctAnswerIndex: -1,
    review: "",
    score: 0,
  },
  next: {
    index: 0,
    question: "",
    options: [],
  },
  more: true,
  total: 0,
  current: 0,
}

interface ChallengeProps {
  id: string
  name: string
  course: string
  userActivityId: string
}

export default function Challenge({ id, name, course, userActivityId }: ChallengeProps) {
  const navigator = useNavigate()
  const createSessionMutation = useCreateChellangeStudySession()
  const updateSessionMutation = useUpdateChellangeStudySession()
  const [activity, setActivity] = useState<ChallengeActivity>(activitiyInitial)
  const [activityTemp, setActivityTemp] = useState<ChallengeActivity>(activitiyInitial)
  const [displayReview, setDisplayReview] = useState(false)
  const { onOpen } = useLeftActivityAlertStore()
  const handleRefetchQueries = useInvalidateStudyPlannerQueries()
  const { setCurrentStep } = useProductTourV2()
  const { user } = useAuth()

  // Use the hook to prevent navigation via browser controls
  usePreventNavigation()

  useEffect(() => {
    createStudySession()
  }, [])

  const createStudySession = async () => {
    try {
      const params = { id, body: { userActivityId } }
      const resp = await createSessionMutation.mutateAsync(params)
      setActivity({
        ...activity,
        ...resp.data.description,
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleonClickSendAnswer = async (answerIndex: number) => {
    try {
      const resp = await updateSessionMutation.mutateAsync({
        sessionId: id,
        activityId: activity.id,
        answerIndex,
      })

      setActivityTemp(resp.data.description)
      setDisplayReview(true)
    } catch (err) {
      console.error(err)
    }
  }

  const handleOnClickNextQuestion = async () => {
    try {
      setActivityTemp((temp) => {
        setActivity(temp)
        setDisplayReview(false)
        return activitiyInitial
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleOnContinueStudying = () => {
    handleRefetchQueries()
    if (user.productTour === null) {
      setCurrentStep(PRODUCT_TOUR_V2_STEPS.MY_SITUATION)
      navigator("/my-situation")
      return
    }
    navigator("/study")
  }

  const handleOnRetryDiagnostic = () => {
    createStudySession()
  }

  const handleOnClose = () => {
    handleRefetchQueries()
    if (activity.more) {
      onOpen()
      return
    }
    if (user.productTour === null) {
      setCurrentStep(PRODUCT_TOUR_V2_STEPS.MY_SITUATION)
      navigator("/my-situation")
      return
    }
    navigator("/study")
  }

  if (createSessionMutation.isPending) {
    return (
      <section className="flex size-full flex-col items-center justify-center">Cargando...</section>
    )
  }

  if (activity.more) {
    return (
      <section className="flex size-full flex-col bg-white p-2 lg:p-10">
        <Header>
          <ChallengeQuestionsHeader
            activity={activity}
            courseName={course}
            activityName={name}
            onClose={handleOnClose}
          />
        </Header>
        <Content>
          <ChallengeQuestions
            activity={activity}
            review={activityTemp.review}
            displayReview={displayReview}
            isLoading={updateSessionMutation.isPending}
            onClickSendAnswer={handleonClickSendAnswer}
            onClickNextQuestion={handleOnClickNextQuestion}
          />
        </Content>
      </section>
    )
  }

  return (
    <section className="flex min-h-screen w-full flex-col bg-gradient-to-b from-primary-50 to-white p-2 lg:p-10">
      <Header>
        <ChallengeFeedbackHeader onClose={handleOnClose} />
      </Header>
      <Content>
        <ChallengeFeedbackContent feedback={activity.feedback} />
      </Content>
      <Footer>
        <ChallengeFeedbackFooter
          activity={activity}
          onRetry={handleOnRetryDiagnostic}
          onContinueStudying={handleOnContinueStudying}
        />
      </Footer>
    </section>
  )
}
